import { watch } from 'vue';
import {
  useCollaborationSocket,
  type RealtimeEvent,
  type ResourceChangedEvent,
} from '@/composables/useCollaborationSocket';
import { useCollaborationStore } from './collaboration.store';

interface ResourceStore {
  handleRealtimeEvent(event: ResourceChangedEvent): Promise<void>;
}
const stores = new Map<string, ResourceStore[]>();
const eventHandlers = new Map<string, Array<(event: RealtimeEvent) => void>>();
let started = false;

export function registerResourceStore(resourceType: string, store: ResourceStore): void {
  const current = stores.get(resourceType) ?? [];
  if (!current.includes(store)) current.push(store);
  stores.set(resourceType, current);
}
export function registerRealtimeEventHandler(
  type: string,
  handler: (event: RealtimeEvent) => void,
): void {
  const handlers = eventHandlers.get(type) ?? [];
  if (!handlers.includes(handler)) handlers.push(handler);
  eventHandlers.set(type, handlers);
}

/** 应在 App 根部仅调用一次；页面不直接订阅 WebSocket。 */
export function startRealtimeDispatcher(): void {
  if (started) return;
  started = true;
  const collaboration = useCollaborationStore();
  const { connected, connect, onRealtimeEvent } = useCollaborationSocket();
  connect();
  onRealtimeEvent((event) => dispatch(event, collaboration));
  watch(connected, (value) => collaboration.setConnected(value), { immediate: true });
}

function dispatch(
  event: RealtimeEvent,
  collaboration: ReturnType<typeof useCollaborationStore>,
): void {
  for (const handler of eventHandlers.get(event.type) ?? []) handler(event);
  if (event.type === 'resource.changed.v1') {
    const resourceEvent = event as unknown as ResourceChangedEvent;
    for (const store of stores.get(resourceEvent.payload.resourceType) ?? [])
      void store.handleRealtimeEvent(resourceEvent);
    return;
  }
  if (event.type === 'presence.changed') collaboration.updatePresence(event.payload as never);
  else if (event.type === 'lock.changed') collaboration.updateLock(event.payload as never);
  else if (event.type === 'collaboration.snapshot')
    collaboration.applySnapshot(event.payload as never);
  else if (event.type === 'realtime.snapshot.required') collaboration.clearWorkspaceState();
}
